import React, { useEffect, useState, useRef } from 'react';
import { Home, ChevronDown, Sparkles, Camera, BookOpen, Users } from 'lucide-react';
interface NavigationDropdownProps {
  onNavigateHome: () => void; // Main button action (context-aware)
  onNavigateLanding?: () => void; // Always goes to landing page (for dropdown)
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
  defaultAction?: 'home' | 'browse'; // What the main button does
  currentContext?: string; // Optional label for current page
}
export function NavigationDropdown({
  onNavigateHome,
  onNavigateLanding,
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests,
  defaultAction = 'home',
  currentContext
}: NavigationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  const mainButtonAction = defaultAction === 'browse' ? onBrowseRecipes : onNavigateHome;
  const mainButtonIcon = defaultAction === 'browse' ? BookOpen : Home;
  const mainButtonLabel = defaultAction === 'browse' ? 'Browse' : 'Home';
  const MainIcon = mainButtonIcon;
  // Show "Home" in dropdown only if main button goes to browse
  const showHomeInDropdown = defaultAction === 'browse';
  return <div className="relative inline-block" ref={dropdownRef} data-id="element-156">
      {/* Split Button - Fixed width */}
      <div className="flex border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-157">
        {/* Main Button */}
        <button onClick={mainButtonAction} className="flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 transition-colors" title={mainButtonLabel} data-id="element-158">
          <MainIcon className="w-4 h-4 text-gray-600" data-id="element-159" />
          <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-160">
            {mainButtonLabel}
          </span>
        </button>

        {/* Divider */}
        <div className="w-px bg-gray-300" data-id="element-161"></div>

        {/* Dropdown Toggle */}
        <button onClick={() => setIsOpen(!isOpen)} className="px-2 py-2 hover:bg-gray-50 transition-colors" aria-label="More navigation options" data-id="element-162">
          <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-163" />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && <div className="absolute top-full left-0 mt-2 w-64 bg-white border-2 border-gray-800 shadow-lg z-20" data-id="element-164">
          {/* Show Home option if main button goes to browse */}
          {showHomeInDropdown && onNavigateLanding && <button onClick={() => {
        onNavigateLanding();
        setIsOpen(false);
      }} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-200" data-id="element-165">
              <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-166">
                <Home className="w-5 h-5 text-gray-600" data-id="element-167" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-168">
                <p className="font-bold text-sm text-gray-900" data-id="element-169">Home</p>
                <p className="text-xs font-mono text-gray-500" data-id="element-170">Landing page</p>
              </div>
            </button>}

          <button onClick={() => {
        onGenerateRecipe();
        setIsOpen(false);
      }} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-200" data-id="element-171">
            <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-172">
              <Sparkles className="w-5 h-5 text-gray-600" data-id="element-173" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-174">
              <p className="font-bold text-sm text-gray-900" data-id="element-175">Generate Recipe</p>
              <p className="text-xs font-mono text-gray-500" data-id="element-176">
                AI-powered creation
              </p>
            </div>
          </button>

          <button onClick={() => {
        onImportRecipe();
        setIsOpen(false);
      }} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-200" data-id="element-177">
            <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-178">
              <Camera className="w-5 h-5 text-gray-600" data-id="element-179" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-180">
              <p className="font-bold text-sm text-gray-900" data-id="element-181">Import Recipe</p>
              <p className="text-xs font-mono text-gray-500" data-id="element-182">Photo or text</p>
            </div>
          </button>

          {/* Show Browse option if main button goes to home */}
          {!showHomeInDropdown && <button onClick={() => {
        onBrowseRecipes();
        setIsOpen(false);
      }} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-200" data-id="element-183">
              <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-184">
                <BookOpen className="w-5 h-5 text-gray-600" data-id="element-185" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-186">
                <p className="font-bold text-sm text-gray-900" data-id="element-187">
                  Browse Recipes
                </p>
                <p className="text-xs font-mono text-gray-500" data-id="element-188">
                  Your collection
                </p>
              </div>
            </button>}

          <button onClick={() => {
        onManageGuests();
        setIsOpen(false);
      }} className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left" data-id="element-189">
            <div className="w-10 h-10 border border-gray-300 bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-190">
              <Users className="w-5 h-5 text-gray-600" data-id="element-191" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-192">
              <p className="font-bold text-sm text-gray-900" data-id="element-193">Manage Guests</p>
              <p className="text-xs font-mono text-gray-500" data-id="element-194">
                Profiles & preferences
              </p>
            </div>
          </button>
        </div>}
    </div>;
}